import { SignInButton, SignUpButton } from "@clerk/react";
import HowItWorks from "../components/info/how-it-works.component";
import Logo from "../components/header/logo.component";
import StockCard from "../components/stock/stock.component";
import PageBackdrop from "../components/ui/PageBackdrop";
import { useEffect, useState, useRef } from "react";

const LandingPage = () => {
  const [stocks, setStocks] = useState([]);
  const [stocksLoading, setStocksLoading] = useState(true);
  const shortListRef = useRef(null);

  useEffect(() => {
    const fetchStocks = async () => {
      try {
        const url = process.env.REACT_APP_API_BASE_URL + "/stock/short-list";
        const res = await fetch(url);
        const data = await res.json();
        setStocks(data);
      } catch (e) {
        console.error("Error fetching stocks:", e);
      } finally {
        setStocksLoading(false);
      }
    };
    fetchStocks();
  }, []);

  const scrollToList = () => {
    shortListRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .lnd-root {
          background: var(--bg);
          color: var(--text-primary);
          font-family: var(--font-sans);
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          position: relative;
          overflow: hidden;
        }

        /* ── Nav ── */
        .lnd-nav {
          position: relative;
          z-index: 10;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 40px;
          border-bottom: 1px solid var(--border-subtle);
        }

        .lnd-nav-brand { display: flex; align-items: center; gap: 10px; }

        .lnd-nav-name {
          font-family: var(--font-mono);
          font-size: 13px;
          font-weight: 500;
          color: var(--text-primary);
          letter-spacing: 0.02em;
        }

        .lnd-nav-actions { display: flex; align-items: center; gap: 8px; }

        .lnd-btn-ghost {
          font-family: var(--font-sans);
          font-size: 13px;
          font-weight: 500;
          color: var(--text-tertiary);
          background: none;
          border: 1px solid var(--border-subtle);
          padding: 7px 16px;
          border-radius: 6px;
          cursor: pointer;
          transition: color 0.15s, border-color 0.15s;
        }
        .lnd-btn-ghost:hover { color: var(--text-primary); border-color: var(--border-strong); }

        .lnd-btn-green {
          font-family: var(--font-sans);
          font-size: 13px;
          font-weight: 600;
          color: var(--accent-ink);
          background: var(--accent);
          border: none;
          padding: 7px 18px;
          border-radius: 6px;
          cursor: pointer;
          transition: background 0.15s;
        }
        .lnd-btn-green:hover { background: var(--accent-hover); }

        /* ── Hero ── */
        .lnd-hero {
          position: relative;
          z-index: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 80px 24px 60px;
        }

        .lnd-hero-inner {
          max-width: 680px;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 22px;
        }

        .lnd-eyebrow {
          font-family: var(--font-mono);
          font-size: 12px;
          color: var(--accent);
          letter-spacing: 0.1em;
          text-transform: uppercase;
          opacity: 0;
          animation: fadeUp 0.4s 0.05s ease forwards;
        }

        .lnd-h1 {
          font-family: 'Instrument Serif', serif;
          font-size: clamp(40px, 7vw, 68px);
          font-weight: 400;
          line-height: 1.06;
          letter-spacing: -0.03em;
          color: var(--text-primary);
          opacity: 0;
          animation: fadeUp 0.4s 0.12s ease forwards;
        }
        .lnd-h1 em { font-style: italic; color: var(--accent); }

        .lnd-sub {
          font-size: 17px;
          color: var(--text-secondary);
          line-height: 1.75;
          max-width: 500px;
          opacity: 0;
          animation: fadeUp 0.4s 0.2s ease forwards;
        }

        .lnd-divider {
          width: 40px;
          height: 1px;
          background: var(--border-subtle);
          opacity: 0;
          animation: fadeUp 0.4s 0.26s ease forwards;
        }

        .lnd-cta-row {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          justify-content: center;
          opacity: 0;
          animation: fadeUp 0.4s 0.3s ease forwards;
        }

        .lnd-btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 28px;
          background: var(--accent);
          color: var(--accent-ink);
          font-weight: 700;
          font-size: 14px;
          border-radius: 7px;
          border: none;
          cursor: pointer;
          transition: background 0.15s, transform 0.15s, box-shadow 0.15s;
          font-family: var(--font-sans);
        }
        .lnd-btn-primary:hover {
          background: var(--accent-hover);
          transform: translateY(-1px);
          box-shadow: 0 8px 28px rgba(52,211,153,0.18);
        }

        .lnd-btn-secondary {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 12px 24px;
          background: transparent;
          color: var(--text-tertiary);
          font-weight: 500;
          font-size: 14px;
          border-radius: 7px;
          border: 1px solid var(--border-subtle);
          cursor: pointer;
          transition: border-color 0.15s, color 0.15s;
          font-family: var(--font-sans);
        }
        .lnd-btn-secondary:hover { border-color: var(--border-strong); color: var(--text-primary); }

        .lnd-note {
          font-size: 12px;
          color: var(--text-tertiary);
          font-family: var(--font-mono);
          letter-spacing: 0.04em;
          opacity: 0;
          animation: fadeUp 0.4s 0.36s ease forwards;
        }

        /* ── Run models callout ── */
        .lnd-models-banner {
          position: relative;
          z-index: 1;
          max-width: 860px;
          width: calc(100% - 48px);
          margin: 0 auto 56px;
          border: 1px solid rgba(52,211,153,0.2);
          border-radius: 14px;
          background: linear-gradient(135deg, rgba(52,211,153,0.05) 0%, rgba(8,11,15,0) 60%);
          padding: 28px 32px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
          flex-wrap: wrap;
        }

        .lnd-models-left { display: flex; flex-direction: column; gap: 8px; flex: 1; min-width: 240px; }

        .lnd-models-eyebrow {
          font-family: var(--font-mono);
          font-size: 12px;
          color: var(--accent);
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }

        .lnd-models-title {
          font-family: 'Instrument Serif', serif;
          font-size: clamp(18px, 2.5vw, 24px);
          font-weight: 400;
          color: var(--text-primary);
          line-height: 1.2;
          letter-spacing: -0.02em;
        }
        .lnd-models-title em { font-style: italic; color: var(--accent); }

        .lnd-models-desc {
          font-size: 13px;
          color: var(--text-tertiary);
          line-height: 1.6;
          font-weight: 400;
          max-width: 420px;
        }

        .lnd-models-features {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 4px;
        }

        .lnd-models-chip {
          font-family: var(--font-mono);
          font-size: 12px;
          padding: 4px 10px;
          border-radius: 100px;
          border: 1px solid rgba(52,211,153,0.2);
          background: rgba(52,211,153,0.06);
          color: var(--accent);
          letter-spacing: 0.04em;
        }

        /* ── Short list section ── */
        .lnd-shortlist {
          position: relative;
          z-index: 1;
          max-width: 900px;
          width: calc(100% - 48px);
          margin: 0 auto;
          padding-bottom: 24px;
          scroll-margin-top: 32px;
        }

        .lnd-shortlist-header {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          margin-bottom: 16px;
          gap: 16px;
          flex-wrap: wrap;
        }

        .lnd-shortlist-header-left { display: flex; flex-direction: column; gap: 6px; }

        .lnd-section-eyebrow {
          font-family: var(--font-mono);
          font-size: 12px;
          color: var(--accent);
          letter-spacing: 0.12em;
          text-transform: uppercase;
          display: flex;
          align-items: center;
          gap: 7px;
        }

        .lnd-section-eyebrow-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--accent);
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.2; }
        }

        .lnd-section-title {
          font-family: 'Instrument Serif', serif;
          font-size: clamp(22px, 3.5vw, 30px);
          font-weight: 400;
          color: var(--text-primary);
          letter-spacing: -0.02em;
          line-height: 1.1;
        }
        .lnd-section-title em { font-style: italic; color: var(--accent); }

        /* ── Gate ── */
        .lnd-gate-wrap { position: relative; }

        .lnd-gate-cards { display: flex; flex-direction: column; gap: 8px; }

        /* First card fully visible, second fading, third blurred */
        .lnd-gate-cards > :nth-child(2) {
          opacity: 0.45;
          filter: blur(2px);
          pointer-events: none;
          user-select: none;
        }

        .lnd-gate-cards > :nth-child(3) {
          opacity: 0.15;
          filter: blur(5px);
          pointer-events: none;
          user-select: none;
        }

        .lnd-gate {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 300px;
          background: linear-gradient(
            to bottom,
            transparent 0%,
            rgba(8,11,15,0.5) 25%,
            rgba(8,11,15,0.95) 60%,
            #080b0f 100%
          );
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-end;
          padding-bottom: 32px;
          gap: 12px;
          z-index: 2;
        }

        .lnd-gate-lock {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: 1px solid rgba(52,211,153,0.2);
          background: rgba(52,211,153,0.06);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 2px;
        }

        .lnd-gate-title {
          font-family: 'Instrument Serif', serif;
          font-size: 22px;
          font-weight: 400;
          color: var(--text-primary);
          letter-spacing: -0.02em;
          text-align: center;
        }

        .lnd-gate-label {
          font-family: var(--font-mono);
          font-size: 12px;
          color: var(--text-tertiary);
          letter-spacing: 0.06em;
          text-align: center;
        }

        .lnd-gate-cta {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 13px 36px;
          background: var(--accent);
          color: var(--accent-ink);
          font-weight: 700;
          font-size: 15px;
          border-radius: 9px;
          border: none;
          cursor: pointer;
          font-family: var(--font-sans);
          transition: background 0.15s, transform 0.15s, box-shadow 0.15s;
          box-shadow: 0 0 40px rgba(52,211,153,0.22);
          margin-top: 4px;
        }
        .lnd-gate-cta:hover {
          background: var(--accent-hover);
          transform: translateY(-1px);
          box-shadow: 0 8px 40px rgba(52,211,153,0.3);
        }

        .lnd-gate-signin {
          font-size: 12px;
          color: var(--text-tertiary);
          font-family: var(--font-mono);
          letter-spacing: 0.04em;
        }
        .lnd-gate-signin button {
          background: none;
          border: none;
          color: var(--text-tertiary);
          cursor: pointer;
          font-family: inherit;
          font-size: inherit;
          letter-spacing: inherit;
          text-decoration: underline;
          text-underline-offset: 3px;
          padding: 0;
        }
        .lnd-gate-signin button:hover { color: var(--text-primary); }

        /* Skeleton */
        .lnd-skel-card {
          background: var(--surface-1);
          border: 1px solid var(--border-subtle);
          border-radius: 12px;
          padding: 16px 18px;
          display: flex;
          align-items: center;
          gap: 14px;
        }
        .lnd-skel-circle {
          width: 44px; height: 44px;
          border-radius: 50%;
          background: var(--border-subtle);
          flex-shrink: 0;
          animation: shimmer 1.4s ease-in-out infinite;
        }
        .lnd-skel-lines { flex: 1; display: flex; flex-direction: column; gap: 8px; }
        .lnd-skel-line {
          height: 10px;
          border-radius: 5px;
          background: var(--border-subtle);
          animation: shimmer 1.4s ease-in-out infinite;
        }
        @keyframes shimmer {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.9; }
        }

        /* ── Feature boxes ── */
        .lnd-features {
          position: relative;
          z-index: 1;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          max-width: 860px;
          width: calc(100% - 48px);
          margin: 48px auto 0;
          gap: 1px;
          background: var(--border-subtle);
          border: 1px solid var(--border-subtle);
          border-radius: 12px;
          overflow: hidden;
        }

        .lnd-feature {
          background: var(--surface-1);
          padding: 22px 20px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          transition: background 0.15s;
        }
        .lnd-feature:hover { background: var(--surface-2); }

        .lnd-feature-icon {
          width: 30px; height: 30px;
          display: flex; align-items: center; justify-content: center;
          background: rgba(52,211,153,0.08);
          border-radius: 7px;
          border: 1px solid rgba(52,211,153,0.15);
          flex-shrink: 0;
        }
        .lnd-feature-title { font-size: 13px; font-weight: 600; color: var(--text-primary); letter-spacing: -0.01em; }
        .lnd-feature-desc { font-size: 12px; color: var(--text-tertiary); line-height: 1.6; }

        /* ── Footer ── */
        .lnd-footer {
          position: relative;
          z-index: 1;
          padding: 24px 40px;
          border-top: 1px solid var(--border-subtle);
          margin-top: 64px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .lnd-footer-note {
          font-family: var(--font-mono);
          font-size: 12px;
          color: var(--text-tertiary);
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        @media (max-width: 700px) {
          .lnd-features { grid-template-columns: repeat(2, 1fr); width: calc(100% - 32px); }
          .lnd-shortlist { width: calc(100% - 32px); }
          .lnd-models-banner { width: calc(100% - 32px); padding: 22px 20px; }
        }

        @media (max-width: 500px) {
          .lnd-nav { padding: 0 20px; }
          .lnd-features { grid-template-columns: 1fr; }
          .lnd-footer { padding: 20px; flex-direction: column; gap: 6px; align-items: flex-start; }
          .lnd-cta-row { flex-direction: column; align-items: stretch; }
          .lnd-btn-primary, .lnd-btn-secondary { justify-content: center; }
        }
      `}</style>

      <div className="lnd-root">
        <PageBackdrop variant="hero" />

        {/* ── Nav ── */}
        <nav className="lnd-nav">
          <div className="lnd-nav-brand">
            <Logo size={28} />
            <span className="lnd-nav-name">Max's Stock Machine</span>
          </div>
          <div className="lnd-nav-actions">
            <SignInButton mode="modal">
              <button className="lnd-btn-ghost">Sign In</button>
            </SignInButton>
            <SignUpButton mode="modal">
              <button className="lnd-btn-green">Subscribe</button>
            </SignUpButton>
          </div>
        </nav>

        {/* ── Hero ── */}
        <section className="lnd-hero">
          <div className="lnd-hero-inner">
            <span className="lnd-eyebrow">
              Daily Stock Discovery · Automated
            </span>

            <h1 className="lnd-h1">
              Find the best stocks in minutes.
              <br />
              Not <em>hours.</em>
            </h1>

            <p className="lnd-sub">
              Every day, Max's Stock Machine scans the entire U.S. market and
              surfaces the highest-quality, undervalued stocks — ranked,
              explained, and ready to act on.
            </p>

            <div className="lnd-divider" />

            <div className="lnd-cta-row">
              <SignUpButton mode="modal">
                <button className="lnd-btn-primary">
                  Subscribe
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                    <path
                      d="M2.5 6.5h8M6.5 2.5l4 4-4 4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </SignUpButton>
              <button className="lnd-btn-secondary" onClick={scrollToList}>
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <path
                    d="M6.5 2v9M2.5 7.5l4 4 4-4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                See a sample
              </button>
            </div>
          </div>
        </section>

        {/* ── How It Works ── */}
        <HowItWorks />

        {/* ── Run models on any stock callout ── */}
        <div className="lnd-models-banner">
          <div className="lnd-models-left">
            <span className="lnd-models-eyebrow">Subscriber feature</span>
            <h3 className="lnd-models-title">
              Run our models on <em>any stock.</em>
            </h3>
            <p className="lnd-models-desc">
              Subscribers can search any U.S.-listed ticker and instantly run
              the same DCF, EPV, and business quality models we use every day —
              on demand, for any company you're researching.
            </p>
            <div className="lnd-models-features">
              <span className="lnd-models-chip">DCF Valuation</span>
              <span className="lnd-models-chip">EPV Model</span>
              <span className="lnd-models-chip">Quality Score</span>
              <span className="lnd-models-chip">Risk Flags</span>
              <span className="lnd-models-chip">Analyst Notes</span>
            </div>
          </div>
          <SignUpButton mode="modal">
            <button
              className="lnd-btn-primary"
              style={{ flexShrink: 0, alignSelf: "center" }}
            >
              Subscribe to unlock
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                <path
                  d="M2.5 6.5h8M6.5 2.5l4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </SignUpButton>
        </div>

        {/* ── Short list preview ── */}
        <div className="lnd-shortlist" ref={shortListRef}>
          <div className="lnd-shortlist-header">
            <div className="lnd-shortlist-header-left">
              <span className="lnd-section-eyebrow">
                <span className="lnd-section-eyebrow-dot" />
                Live · Updated today
              </span>
              <h2 className="lnd-section-title">
                A glimpse at today's <em>picks</em>
              </h2>
            </div>
          </div>

          <div className="lnd-gate-wrap">
            <div className="lnd-gate-cards">
              {stocksLoading
                ? [1, 2, 3].map((i) => (
                    <div className="lnd-skel-card" key={i}>
                      <div className="lnd-skel-circle" />
                      <div className="lnd-skel-lines">
                        <div
                          className="lnd-skel-line"
                          style={{ width: "28%" }}
                        />
                        <div
                          className="lnd-skel-line"
                          style={{ width: "50%" }}
                        />
                      </div>
                      <div
                        className="lnd-skel-line"
                        style={{ width: 56, height: 10 }}
                      />
                    </div>
                  ))
                : stocks.map((stock) => (
                    <StockCard key={stock.ticker} stock={stock} open={true} />
                  ))}
            </div>

            {!stocksLoading && stocks.length > 0 && (
              <div className="lnd-gate">
                <div className="lnd-gate-lock">
                  <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                    <rect
                      x="3"
                      y="6.5"
                      width="9"
                      height="7"
                      rx="1.5"
                      stroke="#34d399"
                      strokeWidth="1.3"
                    />
                    <path
                      d="M5 6.5V4.5a2.5 2.5 0 015 0v2"
                      stroke="#34d399"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <div className="lnd-gate-title">Unlock the full list</div>
                <span className="lnd-gate-label">
                  Subscribe to see every stock that passed the screen today
                </span>
                <SignUpButton mode="modal">
                  <button className="lnd-gate-cta">
                    Subscribe
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path
                        d="M2.5 7h9M7.5 2.5l4.5 4.5-4.5 4.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </SignUpButton>
                <span className="lnd-gate-signin">
                  Already subscribed?{" "}
                  <SignInButton mode="modal">
                    <button>Sign in</button>
                  </SignInButton>
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ── Feature boxes ── */}
        <div className="lnd-features">
          <div className="lnd-feature">
            <div className="lnd-feature-icon">
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                <path
                  d="M2 11l3.5-3.5 2.5 2.5L12 3"
                  stroke="#34d399"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="lnd-feature-title">Daily Stock List</div>
            <div className="lnd-feature-desc">
              Every stock that passed the screen, ranked fresh each morning.
            </div>
          </div>
          <div className="lnd-feature">
            <div className="lnd-feature-icon">
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                <circle
                  cx="6.5"
                  cy="6.5"
                  r="4"
                  stroke="#34d399"
                  strokeWidth="1.4"
                />
                <path
                  d="M9.5 9.5l3 3"
                  stroke="#34d399"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div className="lnd-feature-title">Run Models on Any Stock</div>
            <div className="lnd-feature-desc">
              Search any ticker and get a full DCF, EPV, and quality analysis
              instantly.
            </div>
          </div>
          <div className="lnd-feature">
            <div className="lnd-feature-icon">
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                <path
                  d="M2 10l3-6 3 4 2-3 3 5"
                  stroke="#34d399"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="lnd-feature-title">Fundamental Valuation</div>
            <div className="lnd-feature-desc">
              DCF and earnings-power models, not just multiples.
            </div>
          </div>
          <div className="lnd-feature">
            <div className="lnd-feature-icon">
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                <rect
                  x="2"
                  y="2"
                  width="4.5"
                  height="4.5"
                  rx="1"
                  stroke="#34d399"
                  strokeWidth="1.4"
                />
                <rect
                  x="8.5"
                  y="2"
                  width="4.5"
                  height="4.5"
                  rx="1"
                  stroke="#34d399"
                  strokeWidth="1.4"
                />
                <rect
                  x="2"
                  y="8.5"
                  width="4.5"
                  height="4.5"
                  rx="1"
                  stroke="#34d399"
                  strokeWidth="1.4"
                />
                <rect
                  x="8.5"
                  y="8.5"
                  width="4.5"
                  height="4.5"
                  rx="1"
                  stroke="#34d399"
                  strokeWidth="1.4"
                />
              </svg>
            </div>
            <div className="lnd-feature-title">Quality Scores</div>
            <div className="lnd-feature-desc">
              Moat strength, management, and durability in one number.
            </div>
          </div>
        </div>

        <footer className="lnd-footer">
          <span className="lnd-footer-note">Not financial advice</span>
          <span className="lnd-footer-note">
            For informational purposes only
          </span>
        </footer>
      </div>
    </>
  );
};

export default LandingPage;
